import React, { useEffect, useMemo, useState } from "react";

import { useSearchParams } from "react-router-dom";

import loaderLogoUrl from "components/shared/AppLoader/loaderLogoUrl";

import {
    Bar,
    BarFill,
    CloseButton,
    HintText,
    Inner,
    Logo,
    Message,
    Title,
    Wrapper,
} from "./Redirect.styled";

const BUILDING_MESSAGES = [
    "Laying the first brick...",
    "Mixing an extra-strength batch of cement...",
    "Measuring twice, connecting once...",
    "Asking the foreman for a status update...",
    "Leveling the scaffolding...",
    "Wiring things up, carefully...",
    "Almost there, just adding the roof...",
];

const ERROR_MESSAGES = [
    "Uh oh, a brick slipped...",
    "The scaffolding wobbled a bit...",
];

const BRICK_COLORS = ["#ed6c02", "#c2410c"];
const BRICK_COUNT = 12;
const MESSAGE_INTERVAL_MS = 2600;
const SLOW_HINT_DELAY_MS = 20000;

const Redirect = () => {
    const [searchParams] = useSearchParams();
    const hasError = Boolean(searchParams.get("error"));

    const messages = hasError ? ERROR_MESSAGES : BUILDING_MESSAGES;
    const [messageIndex, setMessageIndex] = useState(0);
    const [showSlowHint, setShowSlowHint] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setMessageIndex((prev) => (prev + 1) % messages.length);
        }, MESSAGE_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [messages.length]);

    useEffect(() => {
        const timeout = setTimeout(
            () => setShowSlowHint(true),
            SLOW_HINT_DELAY_MS,
        );
        return () => clearTimeout(timeout);
    }, []);

    const bricks = useMemo(
        () =>
            Array.from({ length: BRICK_COUNT }, (_, index) => ({
                id: index,
                delay: index * 0.15,
                color: BRICK_COLORS[index % BRICK_COLORS.length],
            })),
        [],
    );

    const canCloseManually =
        typeof window !== "undefined" && Boolean(window.opener);

    return (
        <Wrapper role="status" aria-label="Finishing up your connection">
            <Inner>
                <Logo
                    src={loaderLogoUrl}
                    alt="Signal Sight"
                    width="160"
                    height="55"
                    decoding="sync"
                    loading="eager"
                />

                <Bar>
                    <BarFill />
                </Bar>
                <Title>
                    {hasError
                        ? "Something didn't quite connect"
                        : "Building your connection"}
                </Title>
                <Message key={messageIndex}>{messages[messageIndex]}</Message>
                {showSlowHint && (
                    <HintText>
                        This is taking a little longer than usual — you can
                        close this window and try again if it doesn't finish
                        shortly.
                    </HintText>
                )}
                {showSlowHint && canCloseManually && (
                    <CloseButton onClick={() => window.close()}>
                        Close this window
                    </CloseButton>
                )}
            </Inner>
        </Wrapper>
    );
};

export default Redirect;
